import React, { useCallback, useState, Fragment } from 'react';
import Link from 'next/link';
import { Alert } from 'react-bootstrap';
import styled from 'styled-components';
import useTimeout from '@restart/hooks/useTimeout';

import { DELAY } from '@helpers/const';
import { Table } from '@views/components/cart';
import { TABLE_HEADER, ACTION } from '../../intl';

import { Product as CartProduct } from '@views/components/cart';
import { Product } from '@views/components/product/Product';
import { formatCurrencyVN as formatCurrency } from '@helpers/format-currency';
import { INTERNAL_URI, productHref } from '@helpers/route';

import LazyImage from '@views/components/lazy-image';
import Action from './quantity-action';
import { ERROR } from '../../intl';

import useLayoutContext from '@views/layouts/hooks/use-context';
import _get from 'lodash/get';
import _isFunction from 'lodash/isFunction';
import _percentage from '@helpers/percentage';
import { MAX_DIFF_QUANTITY } from '@helpers/const';
import _isContactPrice from '@helpers/is-contact-price';

const TableRowMessage = styled(Table.Row)`
  border: 0;
  & > td {
    @media (max-width: 567px) {
      padding: 0;
      display: flex;
      width: 100%;

      & > div {
        flex: 0 0 100%;
      }
    }
  }
`;

export const Item = ({ item }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchError, setIsFetchError] = useState(false);
  const [fetchMessage, setFetchMessage] = useState(null);

  const {
    checkout: { handleUpdateItem, handleDeleteItem },
  } = useLayoutContext();

  const timer = useTimeout();
  const hideErrorMessage = useCallback(() => {
    setFetchMessage(null);
    setIsFetchError(false);
  }, [isFetchError]);
  const id = _get(item, 'id');
  const productUri = productHref(_get(item, 'id'), _get(item, 'name'));

  const handleDelete = useCallback(
    async (event) => {
      dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      dataLayer.push({
        event: 'removeFromCart',
        ecommerce: {
          remove: {
            // 'remove' actionFieldObject measures.
            products: [
              {
                //  removing a product to a shopping cart.
                name: _get(item, 'name'),
                id: _get(item, 'id'),
                price: parseInt(_get(item, 'price', 0)),
                brand: '',
                category: '',
                variant: '',
                quantity: _get(item, 'quantity', 1),
              },
            ],
          },
        },
      });

      setIsSubmitting(true);
      try {
        await handleDeleteItem({ id });
      } catch (error) {
        setIsFetchError(true);
        setFetchMessage(_get(error, 'graphQLErrors[0].message', ERROR.MESSAGE));
        timer.set(hideErrorMessage, DELAY);
      }
      setIsSubmitting(false);
    },
    [isSubmitting, isFetchError, item]
  );

  const handleUpdate = useCallback(
    async (quantity, cb) => {
      setIsSubmitting(true);
      try {
        await handleUpdateItem({ id, quantity });
      } catch (error) {
        setIsFetchError(true);
        setFetchMessage(_get(error, 'graphQLErrors[0].message', ERROR.MESSAGE));
        timer.set(hideErrorMessage, DELAY);
      }
      setIsSubmitting(false);
    },
    [isSubmitting, isFetchError]
  );

  const price = parseInt(_get(item, 'price', 0));
  const listPrice = parseInt(_get(item, 'listPrice', 0));
  const sale: number = !!listPrice ? listPrice - price : 0;
  const disabled = _get(item, 'disabled', false);
  const quantity = _get(item, 'quantity', 1);
  const actualQuantity = _get(item, 'actualQuantity', 0);
  const diffQuantity = actualQuantity - quantity;
  // const isContact = _isContactPrice(price);
  const isContact = _get(item, 'isContactProduct');

  return (
    <Fragment>
      {isFetchError && (
        <TableRowMessage key={`${_get(item, 'id')}--alert`}>
          <Table.Col colSpan={5}>
            <Alert
              dismissible
              onClose={() => setIsFetchError(false)}
              variant="danger"
            >
              {fetchMessage}
            </Alert>
          </Table.Col>
        </TableRowMessage>
      )}

      <Table.Row key={_get(item, 'id')} disabled={disabled}>
        {/* <Table.Col>
          <form className="form-check">
            <input 
              type="checkbox" 
              className="form-check-input form-check__input"
              value={1}
              // style={{margin: 0}}
              // onChange={handleChange} 
            />
          </form>
        </Table.Col> */}
        <Table.Col product data-title={TABLE_HEADER.PRODUCT}>
          <CartProduct>
            <Link href={INTERNAL_URI.PRODUCT} as={productUri}>
              <a className="cart-product__image">
                <LazyImage
                  className="d-block w-100"
                  alt={_get(item, 'name')}
                  src={_get(item, 'image')}
                />
              </a>
            </Link>
            <CartProduct.Info>
              <Link href={INTERNAL_URI.PRODUCT} as={productUri}>
                <a className="cart-product__name">{_get(item, 'name')}</a>
              </Link>
            </CartProduct.Info>
          </CartProduct>
        </Table.Col>
        <Table.Col price data-title={TABLE_HEADER.PRICE}>
          {isContact ? (
            'Liên hệ'
          ) : (
            <Product>
              <Product.Price>
                <Product.Price.NewPrice>
                  {formatCurrency(price)}
                </Product.Price.NewPrice>
                {!!sale && (
                  <Product.Price.OldPrice>
                    {formatCurrency(listPrice)}
                  </Product.Price.OldPrice>
                )}
              </Product.Price>
            </Product>
          )}
        </Table.Col>
        <Table.Col quantity data-title={TABLE_HEADER.QUANTITY}>
          <Action
            key={`${_get(item, 'id')}-${_get(item, 'quantity')}`}
            quantity={_get(item, 'quantity') || 0}
            handleUpdateItem={handleUpdate}
            disabled={isSubmitting}
          />

          {MAX_DIFF_QUANTITY > diffQuantity && (
            <span className="text-primary font-style-italic">
              <em>
                Số lượng còn lại <b>{_get(item, 'actualQuantity')}</b> sản phẩm
              </em>
            </span>
          )}
        </Table.Col>
        <Table.Col total data-title={TABLE_HEADER.TOTAL}>
          {isContact ? (
            'Liên hệ'
          ) : (
            <Table.Price as="span">
              {formatCurrency(
                parseInt(_get(item, 'price') || 0) *
                  parseInt(_get(item, 'quantity') || 0)
              )}
            </Table.Price>
          )}
        </Table.Col>
        <Table.Col action reversedLink data-title={TABLE_HEADER.ACTION}>
          <a
            style={isSubmitting ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            onClick={handleDelete}
          >
            {ACTION.DELETE}
          </a>
        </Table.Col>
      </Table.Row>

      {disabled && (
        <Table.Row
          key={`${_get(item, 'id')}-message`}
          disabled={!status}
          message
        >
          <Table.Col colSpan={5}>
            <span className="text-primary font-weight-bold">
              Sản phẩm này đang tạm ngừng kinh doanh hoặc không đủ số lượng
            </span>
          </Table.Col>
        </Table.Row>
      )}
    </Fragment>
  );
};

export default Item;
